import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2, RefreshCw } from 'lucide-react';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const inputClass =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/20 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30';
const labelClass = 'mb-1.5 block text-sm font-medium text-slate-800';
const btnPrimary =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-600/20 transition hover:bg-brand-500 disabled:opacity-50';
const btnSecondary =
  'inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:opacity-50';

type Program = {
  id: string;
  isActive: boolean;
  refereeRewardUsd: string;
  referrerRewardUsd: string;
  minQualifyingOrderUsd: string;
  maxAttributionsPerIpPerMonth: number;
  maxReferralsPerReferrerPerMonth: number;
  announcementEnabled: boolean;
  announcementSendAtHour: number;
  announcementSendAtMinute: number;
  announcementDayOfWeek: number | null;
  announcementLastSlot: string | null;
};

export default function WorkspaceReferralsPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [program, setProgram] = useState<Program | null>(null);
  const [stats, setStats] = useState<{
    pending: number;
    qualified: number;
    rejected: number;
    customersWithCode: number;
    activeSignupIntents: number;
    pendingOrdersWithReferralDiscount: number;
  } | null>(null);
  const [rows, setRows] = useState<
    Array<{
      id: string;
      status: string;
      codeUsed: string;
      createdAt: string;
      referrer: { email: string };
      referee: { email: string };
    }>
  >([]);
  const [togglingActive, setTogglingActive] = useState(false);

  const load = useCallback(
    async (opts?: { quiet?: boolean }) => {
      const quiet = opts?.quiet === true;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const [pRes, sRes, aRes] = await Promise.all([
          authorizedFetch(`${API_BASE}/referrals/workspace/program`),
          authorizedFetch(`${API_BASE}/referrals/workspace/stats`),
          authorizedFetch(`${API_BASE}/referrals/workspace/attributions?page=1&pageSize=30`),
        ]);
        if (!pRes.ok) throw new Error('program');
        setProgram((await pRes.json()) as Program);
        if (sRes.ok) {
          const j = (await sRes.json()) as {
            pending: number;
            qualified: number;
            rejected: number;
            customersWithCode: number;
            activeSignupIntents?: number;
            pendingOrdersWithReferralDiscount?: number;
          };
          setStats({
            pending: j.pending,
            qualified: j.qualified,
            rejected: j.rejected,
            customersWithCode: j.customersWithCode,
            activeSignupIntents: j.activeSignupIntents ?? 0,
            pendingOrdersWithReferralDiscount: j.pendingOrdersWithReferralDiscount ?? 0,
          });
        }
        if (aRes.ok) {
          const j = await aRes.json();
          setRows(j.items || []);
        }
      } catch {
        if (!quiet) {
          setError(t('backoffice.referrals_load_error', { defaultValue: 'Unable to load referral settings.' }));
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [authorizedFetch, t],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const toggleProgramActive = async () => {
    if (!program || togglingActive) return;
    const next = !program.isActive;
    setTogglingActive(true);
    setError(null);
    setNotice(null);
    const prev = program.isActive;
    setProgram({ ...program, isActive: next });
    try {
      const res = await authorizedFetch(`${API_BASE}/referrals/workspace/program`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: next }),
      });
      if (!res.ok) throw new Error('toggle');
      setProgram((await res.json()) as Program);
      if (next) {
        setNotice(t('backoffice.referrals_activated', { defaultValue: 'Referral program is now active.' }));
      } else {
        const stRes = await authorizedFetch(`${API_BASE}/referrals/workspace/stats`);
        const wind = stRes.ok
          ? ((await stRes.json()) as {
              pending: number;
              activeSignupIntents?: number;
              pendingOrdersWithReferralDiscount?: number;
            })
          : { pending: 0, activeSignupIntents: 0, pendingOrdersWithReferralDiscount: 0 };
        setNotice(
          t('backoffice.referrals_deactivated_wind_down', {
            defaultValue:
              'Referral program is off for new links and new sign-ups without a saved session. Ongoing: {{pending}} pending referral(s), {{intents}} signup session(s) finishing within 48h, {{checkout}} unpaid checkout(s) with a referral welcome discount.',
            pending: wind.pending,
            intents: wind.activeSignupIntents ?? 0,
            checkout: wind.pendingOrdersWithReferralDiscount ?? 0,
          }),
        );
      }
      void load({ quiet: true });
    } catch {
      setProgram((p) => (p ? { ...p, isActive: prev } : null));
      setError(t('backoffice.referrals_toggle_error', { defaultValue: 'Could not update program status.' }));
    } finally {
      setTogglingActive(false);
    }
  };

  const save = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!program) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    const form = new FormData(e.currentTarget);
    const body = {
      isActive: program.isActive,
      refereeRewardUsd: Number(form.get('refereeRewardUsd')),
      referrerRewardUsd: Number(form.get('referrerRewardUsd')),
      minQualifyingOrderUsd: Number(form.get('minQualifyingOrderUsd')),
      maxAttributionsPerIpPerMonth: Number(form.get('maxAttributionsPerIpPerMonth')),
      maxReferralsPerReferrerPerMonth: Number(form.get('maxReferralsPerReferrerPerMonth')),
      announcementEnabled: (form.get('announcementEnabled') as string) === 'on',
      announcementSendAtHour: Number(form.get('announcementSendAtHour')),
      announcementSendAtMinute: Number(form.get('announcementSendAtMinute')),
      announcementDayOfWeek:
        (form.get('announcementDayOfWeek') as string) === ''
          ? null
          : Number(form.get('announcementDayOfWeek')),
    };
    try {
      const res = await authorizedFetch(`${API_BASE}/referrals/workspace/program`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('save');
      setProgram((await res.json()) as Program);
      setNotice(t('backoffice.referrals_saved', { defaultValue: 'Saved.' }));
      await load();
    } catch {
      setError(t('backoffice.referrals_save_error', { defaultValue: 'Could not save.' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/referrals">
      <main className="workspace-page">
        <section className="rounded-2xl border border-slate-200/90 bg-gradient-to-br from-emerald-50/50 via-white to-slate-50/40 p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-md">
                <Gift className="h-6 w-6" aria-hidden />
              </div>
              <div className="min-w-0">
                <h1 className="workspace-page-title">
                  {t('backoffice.referrals_title', { defaultValue: 'Referral program' })}
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  {t('backoffice.referrals_intro', {
                    defaultValue:
                      'Use the switch on the right to turn the program on or off. Set reward amounts and announcement email timing below. Fraud controls: device + IP fingerprinting and monthly caps.',
                  })}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 lg:shrink-0 lg:pt-0.5">
              {program ? (
                <div
                  className={[
                    'flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 shadow-md ring-1 transition-colors',
                    program.isActive
                      ? 'border-emerald-400/90 bg-white ring-emerald-600/10'
                      : 'border-slate-200 bg-white ring-slate-900/5',
                  ].join(' ')}
                >
                  <div className="text-right leading-tight">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {t('backoffice.referrals_active_heading', { defaultValue: 'Referral program' })}
                    </p>
                    <p
                      className={`text-sm font-semibold ${program.isActive ? 'text-emerald-700' : 'text-slate-600'}`}
                    >
                      {program.isActive
                        ? t('backoffice.referrals_status_on', { defaultValue: 'Active' })
                        : t('backoffice.referrals_status_off', { defaultValue: 'Inactive' })}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={program.isActive}
                    aria-label={t('backoffice.referrals_active', { defaultValue: 'Referral program active' })}
                    disabled={togglingActive}
                    onClick={() => void toggleProgramActive()}
                    className={[
                      'relative inline-flex h-10 w-[3.5rem] shrink-0 items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                      program.isActive ? 'bg-emerald-500' : 'bg-slate-300',
                      togglingActive ? 'opacity-60' : '',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'pointer-events-none inline-block h-8 w-8 rounded-full bg-white shadow-md ring-1 ring-slate-900/5 transition-transform',
                        program.isActive ? 'translate-x-[1.45rem]' : 'translate-x-1',
                      ].join(' ')}
                      aria-hidden
                    />
                  </button>
                </div>
              ) : null}
              <button type="button" className={btnSecondary} onClick={() => void load()} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
                {t('common.refresh', { defaultValue: 'Refresh' })}
              </button>
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        ) : null}
        {notice ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {notice}
          </div>
        ) : null}

        {loading && !program ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" aria-hidden />
          </div>
        ) : program ? (
          <>
            {stats ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {(
                    [
                      ['pending', stats.pending, 'backoffice.referrals_stat_pending'],
                      ['qualified', stats.qualified, 'backoffice.referrals_stat_qualified'],
                      ['rejected', stats.rejected, 'backoffice.referrals_stat_rejected'],
                      ['codes', stats.customersWithCode, 'backoffice.referrals_stat_codes'],
                      ['sessions', stats.activeSignupIntents, 'backoffice.referrals_stat_signup_sessions'],
                      ['checkout', stats.pendingOrdersWithReferralDiscount, 'backoffice.referrals_stat_checkout_queue'],
                    ] as const
                  ).map(([k, v, labelKey]) => (
                    <div
                      key={k}
                      className="rounded-xl border border-slate-200/90 bg-white px-4 py-3 shadow-sm ring-1 ring-slate-900/[0.03]"
                    >
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        {t(labelKey, { defaultValue: k })}
                      </p>
                      <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">{v}</p>
                    </div>
                  ))}
                </div>
                {!program.isActive ? (
                  <p className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-xs leading-relaxed text-amber-950">
                    {t('backoffice.referrals_wind_down_explainer', {
                      defaultValue:
                        'New public invite links are disabled. Customers who already opened registration with a referral (session saved for 48 hours) can still finish sign-up. Referred customers with a pending first order keep their welcome discount until checkout completes or the order is cancelled.',
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}

            <form
              onSubmit={(e) => void save(e)}
              className="workspace-panel space-y-3"
            >
              <h2 className="text-base font-semibold text-slate-900">
                {t('backoffice.referrals_settings', { defaultValue: 'Program settings' })}
              </h2>
              <p className="text-sm text-slate-500">
                {t('backoffice.referrals_settings_active_hint', {
                  defaultValue: 'Turn the program on or off using the switch at the top of the page.',
                })}
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{t('backoffice.referrals_referee_usd', { defaultValue: 'New customer reward (USD)' })}</label>
                  <input
                    className={inputClass}
                    name="refereeRewardUsd"
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={Number(program.refereeRewardUsd)}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('backoffice.referrals_referrer_usd', { defaultValue: 'Referrer reward (USD)' })}</label>
                  <input
                    className={inputClass}
                    name="referrerRewardUsd"
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={Number(program.referrerRewardUsd)}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('backoffice.referrals_min_order', { defaultValue: 'Min first order total (USD, 0 = any)' })}
                  </label>
                  <input
                    className={inputClass}
                    name="minQualifyingOrderUsd"
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={Number(program.minQualifyingOrderUsd)}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('backoffice.referrals_cap_ip', { defaultValue: 'Max referral sign-ups / IP / month' })}
                  </label>
                  <input
                    className={inputClass}
                    name="maxAttributionsPerIpPerMonth"
                    type="number"
                    min={1}
                    defaultValue={program.maxAttributionsPerIpPerMonth}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('backoffice.referrals_cap_referrer', { defaultValue: 'Max referrals / referrer / month' })}
                  </label>
                  <input
                    className={inputClass}
                    name="maxReferralsPerReferrerPerMonth"
                    type="number"
                    min={1}
                    defaultValue={program.maxReferralsPerReferrerPerMonth}
                  />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-5">
                <h3 className="mb-3 text-sm font-semibold text-slate-900">
                  {t('backoffice.referrals_announcement', { defaultValue: 'Customer announcement email (cron)' })}
                </h3>
                <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
                  <input
                    type="checkbox"
                    name="announcementEnabled"
                    defaultChecked={program.announcementEnabled}
                    className="h-4 w-4 rounded"
                  />
                  {t('backoffice.referrals_announcement_on', {
                    defaultValue: 'Send periodic announcement to eligible customers',
                  })}
                </label>
                <p className="mb-3 text-xs text-slate-500">
                  {t('backoffice.referrals_announcement_hint', {
                    defaultValue:
                      'Runs every minute when clock matches; skips customers who already used a referral or were emailed. Configure in Cron jobs for documentation.',
                  })}
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className={labelClass}>{t('backoffice.referrals_ann_hour', { defaultValue: 'Hour (0–23)' })}</label>
                    <input
                      className={inputClass}
                      name="announcementSendAtHour"
                      type="number"
                      min={0}
                      max={23}
                      defaultValue={program.announcementSendAtHour}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{t('backoffice.referrals_ann_min', { defaultValue: 'Minute (0–59)' })}</label>
                    <input
                      className={inputClass}
                      name="announcementSendAtMinute"
                      type="number"
                      min={0}
                      max={59}
                      defaultValue={program.announcementSendAtMinute}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      {t('backoffice.referrals_ann_dow', { defaultValue: 'Weekday (empty = daily)' })}
                    </label>
                    <select
                      className={inputClass}
                      name="announcementDayOfWeek"
                      defaultValue={program.announcementDayOfWeek ?? ''}
                    >
                      <option value="">{t('backoffice.referrals_ann_daily', { defaultValue: 'Every day' })}</option>
                      <option value="0">Sunday</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                    </select>
                  </div>
                </div>
                {program.announcementLastSlot ? (
                  <p className="mt-2 text-xs text-slate-500">
                    {t('backoffice.referrals_last_slot', {
                      defaultValue: 'Last announcement slot: {{slot}}',
                      slot: program.announcementLastSlot,
                    })}
                  </p>
                ) : null}
              </div>

              <button type="submit" className={btnPrimary} disabled={saving}>
                {saving ? t('common.saving') : t('common.save', { defaultValue: 'Save' })}
              </button>
            </form>

            <div className="workspace-panel overflow-x-auto">
              <h2 className="mb-3 text-lg font-semibold text-slate-900">
                {t('backoffice.referrals_recent', { defaultValue: 'Recent attributions' })}
              </h2>
              <table className="min-w-full text-left text-sm">
                <thead className="border-b text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-3">{t('backoffice.referrals_col_referrer', { defaultValue: 'Referrer' })}</th>
                    <th className="py-2 pr-3">{t('backoffice.referrals_col_referee', { defaultValue: 'New customer' })}</th>
                    <th className="py-2 pr-3">{t('backoffice.referrals_col_code', { defaultValue: 'Code' })}</th>
                    <th className="py-2 pr-3">{t('backoffice.referrals_col_status', { defaultValue: 'Status' })}</th>
                    <th className="py-2">{t('backoffice.referrals_col_date', { defaultValue: 'Created' })}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-slate-500">
                        {t('backoffice.referrals_empty', { defaultValue: 'No attributions yet.' })}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.id}>
                        <td className="py-2 pr-3 text-slate-700">{r.referrer.email}</td>
                        <td className="py-2 pr-3 text-slate-700">{r.referee.email}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{r.codeUsed}</td>
                        <td className="py-2 pr-3">{r.status}</td>
                        <td className="py-2 text-xs text-slate-500">{new Date(r.createdAt).toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </main>
    </MainLayout>
  );
}
